import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, ChevronRight, Calendar, Download } from "lucide-react";
import { toast } from "sonner";

/**
 * Full-year Payroll Calendar — visible to both admins and employees.
 * Renders every pay period generated from the company's pay_schedule +
 * first_pay_date + work_week_start, grouped by month for easy scanning.
 * The "Generate next year" button just bumps the year selector — the
 * backend regenerates the periods deterministically on request.
 */
const MONTH_NAMES = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];

function fmt(iso) {
  const d = new Date(iso + "T00:00");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}
function fmtLong(iso) {
  const d = new Date(iso + "T00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

export default function PayrollCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/payroll/calendar?year=${y}`);
      setData(data);
    } catch (e) {
      toast.error("Could not load the calendar");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(year); }, [year, load]);

  const today = new Date().toISOString().slice(0, 10);
  const grouped = useMemo(() => {
    if (!data?.periods) return {};
    const byMonth = {};
    for (const p of data.periods) {
      const m = p.pay_date.slice(0, 7);
      (byMonth[m] ||= []).push(p);
    }
    return byMonth;
  }, [data]);

  const nextPayDate = useMemo(() => {
    if (!data?.periods) return null;
    return data.periods.find((p) => p.pay_date >= today) || null;
  }, [data, today]);

  const downloadCsv = () => {
    if (!data?.periods) return;
    const rows = [["period_start","period_end","pay_date"], ...data.periods.map((p) => [p.period_start, p.period_end, p.pay_date])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `payroll-calendar-${data.year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-10">
      <div className="overline mb-2">§ PAYROLL &gt; CALENDAR</div>
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <h1 className="font-heading font-black text-5xl tracking-tight" data-testid="calendar-title">Payroll Calendar</h1>
        <div className="flex items-center gap-2">
          <button data-testid="calendar-prev-year" onClick={() => setYear(year - 1)} className="border-2 border-ink p-2 hover:bg-ink hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
          <div data-testid="calendar-year" className="font-mono text-xl font-black px-4 py-2 border-2 border-ink min-w-24 text-center">{year}</div>
          <button data-testid="calendar-next-year" onClick={() => setYear(year + 1)} className="border-2 border-ink p-2 hover:bg-ink hover:text-white"><ChevronRight className="w-4 h-4" /></button>
          {isAdmin && (
            <button
              data-testid="calendar-generate-next"
              onClick={() => { setYear(year + 1); toast.success(`Generated ${year + 1} calendar`); }}
              className="hard-btn ml-2 px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2"
              title="Jump the calendar to next year"
            ><Calendar className="w-4 h-4" /> Generate Next Year</button>
          )}
          <button
            data-testid="calendar-download-csv"
            onClick={downloadCsv}
            disabled={!data?.periods?.length}
            className="border-2 border-ink px-4 py-2 font-mono uppercase text-xs tracking-widest inline-flex items-center gap-2 hover:bg-secondary disabled:opacity-40"
          ><Download className="w-4 h-4" /> CSV</button>
        </div>
      </div>

      {data && (
        <div className="hard-card p-4 mb-6 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="calendar-summary">
          <Fact label="Schedule" val={data.pay_schedule?.toUpperCase() || "—"} />
          <Fact label="Periods this year" val={String(data.count)} />
          <Fact label="Week starts on" val={(data.work_week_start || "monday").toUpperCase()} />
          <Fact label="Next pay date" val={nextPayDate ? fmtLong(nextPayDate.pay_date) : "—"} highlight />
        </div>
      )}

      {loading && <div className="p-10 text-center font-mono text-sm text-muted-foreground">Loading calendar…</div>}

      {!loading && data?.periods?.length === 0 && (
        <div className="p-10 text-center font-mono text-sm text-muted-foreground">
          No pay periods for this year. Admins can set a First Pay Date under Company Settings.
        </div>
      )}

      {!loading && data?.periods?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(grouped).sort(([a],[b]) => a.localeCompare(b)).map(([month, rows]) => {
            const [y, m] = month.split("-");
            return (
              <div key={month} className="hard-card p-4" data-testid={`month-${month}`}>
                <div className="overline mb-3">§ {MONTH_NAMES[Number(m) - 1]} {y}</div>
                <table className="w-full font-mono text-xs">
                  <thead className="text-muted-foreground">
                    <tr>
                      <th className="text-left py-1 font-normal">Period</th>
                      <th className="text-right py-1 font-normal">Pay Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((p) => {
                      const isNext = nextPayDate && p.pay_date === nextPayDate.pay_date;
                      const isPast = p.pay_date < today;
                      return (
                        <tr
                          key={p.pay_date}
                          data-testid={`period-${p.pay_date}`}
                          className={`border-t border-ink/20 ${isNext ? "bg-warn/20" : isPast ? "text-muted-foreground" : ""}`}
                        >
                          <td className="py-1.5">{fmt(p.period_start)} → {fmt(p.period_end)}</td>
                          <td className="py-1.5 text-right font-black">
                            {fmt(p.pay_date)}
                            {isNext && <span className="ml-2 px-1.5 py-0.5 bg-warn text-ink text-[9px] uppercase tracking-widest">Next</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Fact({ label, val, highlight = false }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className={`font-mono font-black ${highlight ? "text-2xl text-good" : "text-lg"}`}>{val}</div>
    </div>
  );
}
